
import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Linkedin, Users, MessageCircle, Send, Heart, TrendingUp } from 'lucide-react'

interface LinkedInMetricsProps {
  profiles: any[]
}

export function LinkedInMetrics({ profiles }: LinkedInMetricsProps) {
  const totalProfiles = profiles.length
  const totalDailyCapacity = profiles.reduce((sum, profile) => 
    sum + profile.daily_connections + profile.daily_inmails + profile.daily_direct_messages, 0)
  
  // Calculate connection metrics
  const totalConnectionsSent = profiles.reduce((sum, p) => sum + (p.connections_sent || 0), 0)
  const totalConnectionsAccepted = profiles.reduce((sum, p) => sum + (p.connections_accepted || 0), 0)
  
  // Calculate message metrics
  const totalMessagesSent = profiles.reduce((sum, p) => sum + (p.direct_messages_sent || 0), 0)
  const totalMessageReplies = profiles.reduce((sum, p) => sum + (p.message_replies || 0), 0)
  
  // Calculate InMail metrics
  const totalInMailsSent = profiles.reduce((sum, p) => sum + (p.inmails_sent || 0), 0)
  const totalInMailReplies = profiles.reduce((sum, p) => sum + (p.inmail_replies || 0), 0)
  
  const avgConnectionRate = totalConnectionsSent > 0
    ? ((totalConnectionsAccepted / totalConnectionsSent) * 100).toFixed(1)
    : '0.0'

  const metrics = [
    {
      title: 'Total Profiles',
      value: totalProfiles.toLocaleString(),
      icon: Linkedin,
      description: 'Active LinkedIn profiles'
    },
    {
      title: 'Daily Capacity',
      value: totalDailyCapacity.toLocaleString(),
      icon: Send,
      description: 'Total daily outreach limit'
    },
    {
      title: 'Avg Connection Rate',
      value: `${avgConnectionRate}%`,
      icon: TrendingUp,
      description: 'Average acceptance rate'
    },
    {
      title: 'Total Connections',
      value: `${totalConnectionsSent.toLocaleString()} / ${totalConnectionsAccepted.toLocaleString()}`,
      icon: Users,
      description: 'Connection Requests / Connections Accepted'
    },
    {
      title: 'Messages',
      value: `${totalMessagesSent.toLocaleString()} / ${totalMessageReplies.toLocaleString()}`,
      icon: MessageCircle,
      description: 'Messages Sent / Message Replies'
    },
    {
      title: 'InMails',
      value: `${totalInMailsSent.toLocaleString()} / ${totalInMailReplies.toLocaleString()}`,
      icon: Heart,
      description: 'InMails Sent / InMail Replies'
    }
  ]

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
      {metrics.map((metric, index) => {
        const Icon = metric.icon
        return (
          <Card key={index} className="relative overflow-hidden border bg-muted/30 hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {metric.title}
              </CardTitle>
              <div className="p-2 rounded-lg bg-muted border">
                <Icon className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex flex-col">
                <div className="text-3xl font-bold text-foreground mb-1">
                  {metric.value}
                </div>
                <p className="text-xs text-muted-foreground font-medium">
                  {metric.description}
                </p>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
